import { PATH_DASHBOARD } from '../routes/paths';

const commonTimeline = [
  {
    id: 'contract',
    title: 'Contract reviewed',
    description: 'Route groups and supported statuses were mapped from the unified backend contract.',
    time: 'Ready for progressive wiring',
    color: 'primary',
  },
  {
    id: 'placeholder',
    title: 'Placeholder shell published',
    description: 'This screen is intentionally structured for empty, loading, error, and detail states.',
    time: 'Phase 1 and 2',
    color: 'success',
  },
];

export const moduleRegistry = {
  dashboard: {
    key: 'dashboard',
    title: 'Dashboard',
    shortTitle: 'Dashboard',
    path: PATH_DASHBOARD.dashboard.root,
    navLabel: 'Dashboard',
    navGroup: 'core',
    icon: 'solar:widget-5-bold',
    mode: 'refactored',
    contractStatus: 'placeholder_ready',
    description:
      'Control-plane bootstrap shell for rollout visibility, enabled refactored modules, and placeholder-safe module startup.',
    routeGroups: [
      'GET /v2/admin/control_plane/status',
      'GET /v2/admin/control_plane/modules',
      'GET /v2/admin/control_plane/shell/dashboard',
      'GET /v2/admin/ping',
    ],
    statuses: ['placeholder_ready', 'shadow', 'enforced'],
    bullets: [
      'Use control-plane responses for shell/bootstrap state, not transaction analytics.',
      'Show rollout-state badges like shadow and enforced without collapsing them into generic labels.',
      'Keep the dashboard safe for progressive implementation while backend modules come online.',
    ],
    sampleColumns: [
      { field: 'name', headerName: 'Bootstrap signal', flex: 1.3 },
      { field: 'status', headerName: 'Status', flex: 0.9 },
      { field: 'notes', headerName: 'Notes', flex: 1.8 },
    ],
    sampleRows: [
      {
        id: 'cp-status',
        name: 'Control plane status',
        status: 'active',
        notes: 'Backed by /v2/admin/control_plane/status',
      },
      {
        id: 'cp-modules',
        name: 'Enabled modules',
        status: 'active',
        notes: 'Backed by /v2/admin/control_plane/modules',
      },
    ],
    detailItems: [
      { label: 'Purpose', value: 'Bootstrap and rollout visibility' },
      { label: 'Backend source', value: 'control_plane and ping routes' },
      { label: 'Timeline support', value: 'Enabled by contract shell payload' },
    ],
    timeline: commonTimeline,
  },
  accessControl: {
    key: 'accessControl',
    title: 'Access Control',
    shortTitle: 'Access Control',
    path: PATH_DASHBOARD.accessControl.root,
    navLabel: 'Access Control',
    navGroup: 'core',
    icon: 'solar:shield-user-bold',
    mode: 'refactored',
    description:
      'Governance shell for roles, permissions, staff profiles, access requests, grants, and audit visibility.',
    routeGroups: [
      'GET /v2/admin/access/roles',
      'GET /v2/admin/access/permissions',
      'GET /v2/admin/access/tiers',
      'GET /v2/admin/access/staff',
      'GET /v2/admin/access/staff/{staffId}',
      'PUT /v2/admin/access/staff/{staffId}',
      'GET /v2/admin/access/requests',
      'POST /v2/admin/access/requests',
      'POST /v2/admin/access/requests/{id}/approve',
      'POST /v2/admin/access/requests/{id}/reject',
      'GET /v2/admin/access/grants',
      'POST /v2/admin/access/grants/{id}/revoke',
      'GET /v2/admin/access/audit',
      'GET /v2/admin/control_plane/shell/access',
    ],
    statuses: ['active', 'blocked', 'approved', 'rejected'],
    bullets: [
      'Effective access comes from the governance layer, not only the legacy admin table.',
      'Staff work-hours and after-hours rules belong in profile management states.',
      'Temporary grants and requests need explicit approval and audit visibility.',
    ],
    sampleColumns: [
      { field: 'staffId', headerName: 'Staff ID', flex: 1 },
      { field: 'role', headerName: 'Role', flex: 1 },
      { field: 'status', headerName: 'Status', flex: 0.8 },
      { field: 'window', headerName: 'Access window', flex: 1.4 },
    ],
    sampleRows: [],
    detailItems: [
      { label: 'Primary records', value: 'staff, requests, grants, audit' },
      { label: 'Contract shell', value: 'GET /v2/admin/control_plane/shell/access' },
      { label: 'Priority order', value: 'Phase 3 order: #1' },
    ],
    timeline: commonTimeline,
  },
  riskPolicy: {
    key: 'riskPolicy',
    title: 'Risk & Policy',
    shortTitle: 'Risk & Policy',
    path: PATH_DASHBOARD.riskPolicy.root,
    navLabel: 'Risk & Policy',
    navGroup: 'core',
    icon: 'solar:shield-warning-bold',
    mode: 'refactored',
    description:
      'Decision-log, threshold, simulation, and policy-review shell for operational fraud and review workflows.',
    routeGroups: [
      'GET /v2/admin/risk/decision_logs',
      'GET /v2/admin/risk/summary',
      'GET /v2/admin/risk/timeline/{reference}',
      'GET /v2/admin/risk/third_party_thresholds',
      'PUT /v2/admin/risk/third_party_thresholds',
      'POST /v2/admin/risk/simulate',
      'GET /v2/admin/control_plane/shell/risk',
    ],
    statuses: ['pending', 'awaiting review', 'resolved', 'failed'],
    bullets: [
      'Decision logs, summaries, timelines, and simulations should remain distinct views.',
      'Threshold configuration must preserve per-tier values and active state.',
      'Review-required outcomes should not be merged into generic failures.',
    ],
    sampleColumns: [
      { field: 'scope', headerName: 'Decision scope', flex: 1.1 },
      { field: 'decision', headerName: 'Decision code', flex: 1.8 },
      { field: 'status', headerName: 'Status', flex: 0.9 },
      { field: 'tier', headerName: 'Tier', flex: 0.8 },
    ],
    sampleRows: [
      {
        id: 'sample-risk',
        scope: 'withdrawal',
        decision: 'beneficiary.third_party.within_limit',
        status: 'resolved',
        tier: 'stage_one',
      },
    ],
    detailItems: [
      { label: 'Primary focus', value: 'Policy visibility and threshold governance' },
      { label: 'Timeline path', value: '/v2/admin/risk/timeline/{reference}' },
      { label: 'Priority order', value: 'Phase 3 order: #2' },
    ],
    timeline: commonTimeline,
  },
  users: {
    key: 'users',
    title: 'Users',
    shortTitle: 'Users',
    path: PATH_DASHBOARD.users.root,
    navLabel: 'Users',
    navGroup: 'operations',
    icon: 'solar:users-group-rounded-bold',
    mode: 'refactored',
    description:
      'User controls and primary-account review shell aligned to documented control and review routes.',
    routeGroups: [
      'GET /v2/admin/users/{userId}/controls',
      'POST /v2/admin/users/{userId}/controls',
      'POST /v2/admin/users/controls/{controlId}/resolve',
      'GET /v2/admin/users/{userId}/primary_account',
      'POST /v2/admin/users/{userId}/primary_account/review',
      'POST /v2/admin/users/primary_account_reviews/{reviewId}/approve',
      'POST /v2/admin/users/primary_account_reviews/{reviewId}/reject',
    ],
    statuses: ['active', 'blocked', 'resolved', 'approved', 'rejected'],
    bullets: [
      'User controls and primary-account reviews are separate workflows and should remain separate in the UI.',
      'Resolve, approve, and reject actions need explicit recovery and audit framing.',
      'Primary account match results should surface backend review status without reinterpretation.',
    ],
    sampleColumns: [
      { field: 'control', headerName: 'Control', flex: 1.1 },
      { field: 'status', headerName: 'Status', flex: 0.9 },
      { field: 'reason', headerName: 'Reason', flex: 1.6 },
      { field: 'expires', headerName: 'Expires', flex: 1.1 },
    ],
    sampleRows: [],
    detailItems: [
      { label: 'Primary workflows', value: 'Controls and primary-account review' },
      { label: 'Review actions', value: 'approve, reject, resolve' },
      { label: 'Priority order', value: 'Phase 3 order: #3' },
    ],
    timeline: commonTimeline,
  },
  transactions: {
    key: 'transactions',
    title: 'Transactions',
    shortTitle: 'Transactions',
    path: PATH_DASHBOARD.transactions.root,
    navLabel: 'Transactions',
    navGroup: 'operations',
    icon: 'solar:card-transfer-bold',
    mode: 'refactored',
    description:
      'Transaction probing shell for history, detail, and reference timelines without legacy route assumptions.',
    routeGroups: [
      'GET /v2/admin/transactions/history',
      'GET /v2/admin/transactions/{transactionId}',
      'GET /v2/admin/transactions/timeline/{reference}',
    ],
    statuses: ['queued', 'pending', 'failed', 'cancelled', 'resolved'],
    bullets: [
      'Transaction history, single-transaction detail, and timeline probing are distinct screen states.',
      'Reference timelines should help operators understand what happened and what changed after actions.',
      'Do not map operational payout semantics back onto deprecated transaction screens.',
    ],
    sampleColumns: [
      { field: 'reference', headerName: 'Reference', flex: 1.4 },
      { field: 'type', headerName: 'Type', flex: 1.1 },
      { field: 'status', headerName: 'Status', flex: 0.9 },
      { field: 'amount', headerName: 'Amount', flex: 0.8 },
    ],
    sampleRows: [
      {
        id: 'tx-sample',
        reference: '7b0d...',
        type: 'bank.transfer',
        status: 'pending',
        amount: 'NGN 1,000.00',
      },
    ],
    detailItems: [
      { label: 'Core views', value: 'History, detail, reference timeline' },
      { label: 'Audit goal', value: 'Probe what happened and why' },
      { label: 'Priority order', value: 'Phase 3 order: #4' },
    ],
    timeline: commonTimeline,
  },
  settlementOps: {
    key: 'settlementOps',
    title: 'Settlement Ops',
    shortTitle: 'Settlement Ops',
    path: PATH_DASHBOARD.settlementOps.root,
    navLabel: 'Settlement Ops',
    navGroup: 'operations',
    icon: 'solar:hand-money-bold',
    mode: 'refactored',
    description:
      'Operational shell for settlement overview, queue visibility, failure recovery, and escalation workflows.',
    routeGroups: [
      'GET /v2/admin/settlement/overview',
      'GET /v2/admin/settlement/queue',
      'GET /v2/admin/settlement/failed',
      'GET /v2/admin/settlement/stalled',
      'GET /v2/admin/settlement/recovery_log',
      'POST /v2/admin/settlement/{watchId}/retry',
      'POST /v2/admin/settlement/{watchId}/requeue',
      'POST /v2/admin/settlement/{watchId}/cancel',
      'POST /v2/admin/settlement/{watchId}/escalate',
      'GET /v2/admin/control_plane/shell/settlement',
    ],
    statuses: ['queued', 'failed', 'stalled', 'awaiting review', 'cancelled'],
    bullets: [
      'Retry and requeue actions now create outbox intent and should trigger both settlement and outbox refreshes.',
      'Awaiting-review state should remain separate from failed and stalled items.',
      'Escalation and cancellation need explicit confirmation UX.',
    ],
    sampleColumns: [
      { field: 'watchType', headerName: 'Watch type', flex: 1.3 },
      { field: 'reference', headerName: 'Reference', flex: 1.1 },
      { field: 'status', headerName: 'Status', flex: 0.9 },
      { field: 'resource', headerName: 'Resource', flex: 1.2 },
    ],
    sampleRows: [
      {
        id: 'settlement-sample',
        watchType: 'crypto_settlement_handoff',
        reference: 'quote_123',
        status: 'queued',
        resource: 'pt_lens_instant_swap',
      },
    ],
    detailItems: [
      { label: 'Recovery log', value: 'Documented route available' },
      { label: 'Control-plane shell', value: 'GET /v2/admin/control_plane/shell/settlement' },
      { label: 'Priority order', value: 'Phase 3 order: #5' },
    ],
    timeline: commonTimeline,
  },
  queueJobs: {
    key: 'queueJobs',
    title: 'Queue & Jobs',
    shortTitle: 'Queue & Jobs',
    path: PATH_DASHBOARD.queueJobs.root,
    navLabel: 'Queue & Jobs',
    navGroup: 'operations',
    icon: 'solar:server-square-update-bold',
    mode: 'refactored',
    description:
      'Queue visibility shell for queued, reserved, failed, and outbox jobs with documented recovery actions.',
    routeGroups: [
      'GET /v2/admin/ops/queues/overview',
      'GET /v2/admin/ops/queues/queued',
      'GET /v2/admin/ops/queues/reserved',
      'GET /v2/admin/ops/queues/failed',
      'GET /v2/admin/ops/queues/outbox',
      'POST /v2/admin/ops/queues/failed/{jobId}/retry',
      'POST /v2/admin/ops/queues/queued/{jobId}/cancel',
      'POST /v2/admin/ops/queues/reserved/{jobId}/release',
      'POST /v2/admin/ops/queues/outbox/{jobId}/retry',
      'POST /v2/admin/ops/queues/outbox/{jobId}/cancel',
    ],
    statuses: ['queued', 'reserved', 'failed', 'outbox pending', 'outbox failed', 'cancelled'],
    bullets: [
      'Queue jobs and outbox jobs are related but must remain visually distinct.',
      'Reserved, queued, failed, pending, and cancelled should not be merged into one bucket.',
      'Outbox retry and cancel semantics differ from queue retry/release semantics.',
    ],
    sampleColumns: [
      { field: 'queue', headerName: 'Queue', flex: 1.1 },
      { field: 'status', headerName: 'Status', flex: 0.9 },
      { field: 'attempts', headerName: 'Attempts', flex: 0.7 },
      { field: 'notes', headerName: 'Notes', flex: 1.7 },
    ],
    sampleRows: [
      {
        id: 'queue-sample',
        queue: 'bank_transfers',
        status: 'outbox pending',
        attempts: 0,
        notes: 'Dispatch intent stored before queue handoff',
      },
    ],
    detailItems: [
      { label: 'Native tables', value: 'queue_jobs and queue_jobs_failed' },
      { label: 'Outbox model', value: 'pt_system_outbox_jobs' },
      { label: 'Priority order', value: 'Phase 3 order: #6' },
    ],
    timeline: commonTimeline,
  },
  blockchainOps: {
    key: 'blockchainOps',
    title: 'Blockchain Ops',
    shortTitle: 'Blockchain Ops',
    path: PATH_DASHBOARD.blockchainOps.root,
    navLabel: 'Blockchain Ops',
    navGroup: 'operations',
    icon: 'solar:database-bold',
    mode: 'refactored',
    description:
      'Crypto recovery timeline and blockchain deposit recovery surfaces aligned to the documented transaction timeline contract.',
    routeGroups: [
      'GET /v2/admin/transactions/crypto_timeline/blockchain/{blockchainTxId}',
      'GET /v2/admin/transactions/crypto_timeline/quote/{quoteId}',
      'GET /v2/admin/transactions/crypto_timeline/transaction/{transactionId}',
      'POST /v2/admin/transactions/blockchain/{blockchainTxId}/recover',
    ],
    statuses: [
      'blocked_aml',
      'blocked_on_hold',
      'recovery_in_progress',
      'recovery_failed',
      'pending_dispatch',
      'dispatched',
      'dispatch_failed',
      'not_required',
    ],
    bullets: [
      'Use the blockchain, quote, and lens transaction lookups exactly as documented by the crypto ops contract.',
      'Keep recovery actions explicit and confirmable, and interpret noop as an informational outcome instead of an error.',
      'Keep legacy blockchain tables accessible separately until those screens are intentionally migrated.',
    ],
    sampleColumns: [
      { field: 'lookup', headerName: 'Lookup Surface', flex: 1.3 },
      { field: 'status', headerName: 'Status', flex: 0.9 },
      { field: 'updated', headerName: 'Updated', flex: 1.1 },
      { field: 'notes', headerName: 'Notes', flex: 1.5 },
    ],
    sampleRows: [
      {
        id: 'blockchain-ops-sample',
        lookup: 'crypto recovery timeline',
        status: 'pending_dispatch',
        updated: 'Contract documented',
        notes: 'Timeline and recovery flow ready for progressive wiring',
      },
    ],
    detailItems: [
      { label: 'Primary views', value: 'timeline by blockchain tx, quote, lens transaction' },
      { label: 'Recovery action', value: 'POST /v2/admin/transactions/blockchain/{blockchainTxId}/recover' },
      { label: 'Migration order', value: 'Contract-backed refactor, legacy views remain accessible' },
    ],
    timeline: commonTimeline,
  },
  sos: {
    key: 'sos',
    title: 'SOS',
    shortTitle: 'SOS',
    path: PATH_DASHBOARD.sos.root,
    navLabel: 'SOS',
    navGroup: 'operations',
    icon: 'solar:siren-rounded-bold',
    mode: 'refactored',
    description:
      'Incident-response shell for SOS overview KPIs, filter-driven signal review, drawer detail, and recovery actions.',
    routeGroups: [
      'GET /v2/admin/sos/overview',
      'GET /v2/admin/sos/active',
      'GET /v2/admin/sos',
      'GET /v2/admin/sos/{signalId}',
      'POST /v2/admin/sos/{signalId}/resolve',
      'POST /v2/admin/sos/{signalId}/extend',
      'POST /v2/admin/sos/{signalId}/remove_block',
    ],
    statuses: ['active', 'pending', 'resolved', 'blocked'],
    bullets: [
      'SOS workflows need strong action clarity and safe recovery confirmations.',
      'Active signals and historical review should stay separate but linked.',
      'Remove-block is a risky action and should remain explicit in the UI.',
    ],
    sampleColumns: [
      { field: 'signal', headerName: 'Signal', flex: 1.2 },
      { field: 'status', headerName: 'Status', flex: 0.9 },
      { field: 'user', headerName: 'User', flex: 0.9 },
      { field: 'updated', headerName: 'Updated', flex: 1.1 },
    ],
    sampleRows: [],
    detailItems: [
      { label: 'Views', value: 'overview, signals, drawer detail' },
      { label: 'Actions', value: 'resolve, extend, remove_block' },
      { label: 'Priority order', value: 'Phase 3 order: #7' },
    ],
    timeline: commonTimeline,
  },
  adjustments: {
    key: 'adjustments',
    title: 'Adjustments',
    shortTitle: 'Adjustments',
    path: PATH_DASHBOARD.adjustments.root,
    navLabel: 'Adjustments',
    navGroup: 'operations',
    icon: 'solar:calculator-bold',
    mode: 'refactored',
    description:
      'Request-driven adjustments shell for creation, approval, rejection, execution, and cancellation workflows.',
    routeGroups: [
      'GET /v2/admin/adjustments',
      'POST /v2/admin/adjustments',
      'GET /v2/admin/adjustments/{requestId}',
      'POST /v2/admin/adjustments/{requestId}/approve',
      'POST /v2/admin/adjustments/{requestId}/reject',
      'POST /v2/admin/adjustments/{requestId}/execute',
      'POST /v2/admin/adjustments/{requestId}/cancel',
      'GET /v2/admin/control_plane/shell/adjustments',
    ],
    statuses: ['pending', 'approved', 'rejected', 'cancelled'],
    bullets: [
      'Approval and execution are distinct actions and should remain distinct in the interface.',
      'Cancellation must remain available only where the backend permits it.',
      'This module is part of the initial control-plane shell bootstrap.',
    ],
    sampleColumns: [
      { field: 'request', headerName: 'Request ID', flex: 1.1 },
      { field: 'status', headerName: 'Status', flex: 0.9 },
      { field: 'reason', headerName: 'Reason', flex: 1.5 },
      { field: 'owner', headerName: 'Requested by', flex: 1 },
    ],
    sampleRows: [],
    detailItems: [
      { label: 'Lifecycle', value: 'request, review, execute, cancel' },
      { label: 'Control-plane shell', value: 'GET /v2/admin/control_plane/shell/adjustments' },
      { label: 'Priority order', value: 'Phase 3 order: #8' },
    ],
    timeline: commonTimeline,
  },
  config: {
    key: 'config',
    title: 'Config',
    shortTitle: 'Config',
    path: PATH_DASHBOARD.config.root,
    navLabel: 'Config',
    navGroup: 'platform',
    icon: 'solar:settings-bold',
    mode: 'refactored',
    description:
      'Configuration and feature-flag shell for group reads, item updates, flag changes, audit review, and rollback flows.',
    routeGroups: [
      'GET /v2/admin/config/groups',
      'GET /v2/admin/config/groups/{groupKey}',
      'PUT /v2/admin/config/items/{itemId}',
      'GET /v2/admin/config/feature_flags',
      'PUT /v2/admin/config/feature_flags/{flagKey}',
      'GET /v2/admin/config/audit',
      'POST /v2/admin/config/rollback/{logId}',
      'GET /v2/admin/control_plane/shell/config',
    ],
    statuses: ['active', 'failed', 'approved', 'rejected'],
    bullets: [
      'Feature-flag state and config item state should stay traceable through audit and rollback surfaces.',
      'This module needs explicit confirmation for rollback and risky config changes.',
      'The control-plane shell exposes Config as one of the initial bootstrap modules.',
    ],
    sampleColumns: [
      { field: 'group', headerName: 'Group', flex: 1.1 },
      { field: 'item', headerName: 'Item', flex: 1.4 },
      { field: 'status', headerName: 'Status', flex: 0.9 },
      { field: 'updated', headerName: 'Updated', flex: 1.1 },
    ],
    sampleRows: [],
    detailItems: [
      { label: 'Config surfaces', value: 'groups, items, feature flags, audit' },
      { label: 'Control-plane shell', value: 'GET /v2/admin/control_plane/shell/config' },
      { label: 'Priority order', value: 'Phase 3 order: #9' },
    ],
    timeline: commonTimeline,
  },
  serviceReports: {
    key: 'serviceReports',
    title: 'Service Reports',
    shortTitle: 'Service Reports',
    path: PATH_DASHBOARD.serviceReports.root,
    navLabel: 'Service Reports',
    navGroup: 'platform',
    icon: 'solar:monitor-smartphone-bold',
    mode: 'refactored',
    description:
      'Provider, incident, service overview, and blockchain visibility shell for operational diagnostics.',
    routeGroups: [
      'GET /v2/admin/services/overview',
      'GET /v2/admin/services/provider/{provider}',
      'GET /v2/admin/services/incidents',
      'GET /v2/admin/services/blockchain',
    ],
    statuses: ['active', 'failed', 'degraded'],
    bullets: [
      'Service overview, incidents, provider views, and blockchain visibility should remain separate but navigable together.',
      'This screen is intended for diagnosis and visibility rather than queue recovery.',
      'Backend blockchain visibility now lives under active service and blockchain ops routes.',
    ],
    sampleColumns: [
      { field: 'surface', headerName: 'Surface', flex: 1.3 },
      { field: 'status', headerName: 'Status', flex: 0.9 },
      { field: 'updated', headerName: 'Updated', flex: 1.1 },
      { field: 'notes', headerName: 'Notes', flex: 1.5 },
    ],
    sampleRows: [
      {
        id: 'services-sample',
        surface: 'services blockchain diagnostics',
        status: 'active',
        updated: 'Contract documented',
        notes: 'Ready for progressive wiring',
      },
    ],
    detailItems: [
      { label: 'Primary views', value: 'overview, provider, incidents, blockchain' },
      { label: 'Blockchain route', value: 'GET /v2/admin/services/blockchain' },
      { label: 'Priority order', value: 'Phase 3 order: #10' },
    ],
    timeline: commonTimeline,
  },
  legacyAdminManagement: {
    key: 'legacyAdminManagement',
    title: 'Legacy Admin Management',
    shortTitle: 'Admin Mgmt',
    path: PATH_DASHBOARD.legacy.adminManagement.root,
    navLabel: 'Admin Management',
    navGroup: 'legacy',
    icon: 'solar:user-id-bold',
    mode: 'legacy',
    description:
      'Legacy-active admin management routes kept accessible while governance flows move into Access Control.',
    routeGroups: [
      'GET /v2/admin/admins',
      'POST /v2/admin/admins',
      'POST /v2/admin/admins/update',
      'DELETE /v2/admin/admins/{staffId}',
    ],
    statuses: ['active'],
    bullets: [
      'Keep accessible only while product still depends on these legacy admin-management routes.',
      'Do not re-center new access workflows here; Access Control is the refactored target.',
      'Support-team routes remain deprecated and are intentionally excluded.',
    ],
    sampleColumns: [
      { field: 'routeFamily', headerName: 'Route family', flex: 1.2 },
      { field: 'status', headerName: 'Status', flex: 0.8 },
      { field: 'notes', headerName: 'Notes', flex: 1.7 },
    ],
    sampleRows: [],
    detailItems: [
      { label: 'Category', value: 'Legacy but still active' },
      { label: 'Preferred replacement', value: 'Access Control module' },
      { label: 'Migration order', value: 'After refactored targets' },
    ],
    timeline: commonTimeline,
  },
  legacyCryptoAssets: {
    key: 'legacyCryptoAssets',
    title: 'Legacy Crypto Assets & Ranges',
    shortTitle: 'Crypto Assets',
    path: PATH_DASHBOARD.legacy.cryptoAssets.root,
    navLabel: 'Crypto Assets',
    navGroup: 'legacy',
    icon: 'solar:bitcoin-card-bold',
    mode: 'legacy',
    description:
      'Legacy-active crypto asset and range management routes retained separately from deprecated crypto-rate routes.',
    routeGroups: [
      'GET /v2/admin/cryto_asset',
      'POST /v2/admin/cryto_asset',
      'POST /v2/admin/cryto_asset/update',
      'DELETE /v2/admin/cryto_asset/{id}',
      'PUT /v2/admin/cryto_asset/priority',
      'GET /v2/admin/crypto_range',
      'POST /v2/admin/crypto_range',
      'POST /v2/admin/crypto_range/update',
      'GET /v2/admin/crypto_range/refresh_source',
      'DELETE /v2/admin/crypto_range/{id}',
    ],
    statuses: ['active'],
    bullets: [
      'Keep these routes accessible because they are still active in the contract.',
      'Do not call deprecated /v2/admin/crypto_rate/* endpoints from the refactored shell.',
      'This area should remain clearly marked as legacy-active.',
    ],
    sampleColumns: [
      { field: 'surface', headerName: 'Surface', flex: 1.2 },
      { field: 'status', headerName: 'Status', flex: 0.8 },
      { field: 'notes', headerName: 'Notes', flex: 1.8 },
    ],
    sampleRows: [],
    detailItems: [
      { label: 'Category', value: 'Legacy but still active' },
      { label: 'Deprecated routes removed', value: 'crypto_rate' },
      { label: 'Migration order', value: 'After refactored targets' },
    ],
    timeline: commonTimeline,
  },
  legacyContent: {
    key: 'legacyContent',
    title: 'Legacy Content',
    shortTitle: 'Content',
    path: PATH_DASHBOARD.legacy.content.root,
    navLabel: 'Content',
    navGroup: 'legacy',
    icon: 'solar:document-text-bold',
    mode: 'legacy',
    description:
      'Legacy-active content routes for featured posts and app posts, preserved as accessible operational modules.',
    routeGroups: [
      'POST /v2/admin/featured_posts',
      'GET /v2/admin/featured_posts',
      'DELETE /v2/admin/featured_posts/{id}',
      'POST /v2/admin/featured_posts/update',
      'POST /v2/admin/app_posts',
      'GET /v2/admin/app_posts',
      'DELETE /v2/admin/app_posts/{id}',
      'GET /v2/admin/app_posts/{id}',
      'PUT /v2/admin/app_posts/{id}',
    ],
    statuses: ['active'],
    bullets: [
      'Content management remains active, but it is not part of the first refactored shell bootstrap.',
      'This screen should stay clearly separated from refactored config and control-plane modules.',
      'Placeholder structure is ready for later migration or targeted wiring.',
    ],
    sampleColumns: [
      { field: 'surface', headerName: 'Surface', flex: 1.2 },
      { field: 'status', headerName: 'Status', flex: 0.8 },
      { field: 'notes', headerName: 'Notes', flex: 1.8 },
    ],
    sampleRows: [],
    detailItems: [
      { label: 'Category', value: 'Legacy but still active' },
      { label: 'Active endpoints', value: 'featured_posts and app_posts' },
      { label: 'Migration order', value: 'Remaining legacy-active modules' },
    ],
    timeline: commonTimeline,
  },
  legacyCruiseCampaigns: {
    key: 'legacyCruiseCampaigns',
    title: 'Legacy Cruise & Campaigns',
    shortTitle: 'Cruise',
    path: PATH_DASHBOARD.legacy.cruiseCampaigns.root,
    navLabel: 'Cruise & Campaigns',
    navGroup: 'legacy',
    icon: 'solar:rocket-bold',
    mode: 'legacy',
    description:
      'Legacy-active cruise and notification campaign routes kept reachable while the new shell focuses on refactored targets.',
    routeGroups: [
      'POST /v2/admin/cruise/import',
      'GET /v2/admin/cruise/batch',
      'DELETE /v2/admin/cruise/batch/{id}',
      'GET /v2/admin/cruise',
      'DELETE /v2/admin/cruise/{id}',
      'PUT /v2/admin/cruise/{id}',
      'GET /v2/admin/cruise/history',
      'GET /v2/admin/cruise/telementry',
      'GET /v2/admin/cruise/overview',
      'POST /v2/admin/campaign/affected_users',
      'POST /v2/admin/campaign',
      'GET /v2/admin/campaign',
      'DELETE /v2/admin/campaign/{id}',
      'GET /v2/admin/campaign/{id}/details',
    ],
    statuses: ['active'],
    bullets: [
      'Cruise and campaign tooling remains active and should stay accessible under a legacy grouping.',
      'Telemetry spelling follows the backend contract route as documented.',
      'This placeholder keeps room for later wiring without mixing it into refactored risk or queue flows.',
    ],
    sampleColumns: [
      { field: 'surface', headerName: 'Surface', flex: 1.2 },
      { field: 'status', headerName: 'Status', flex: 0.8 },
      { field: 'notes', headerName: 'Notes', flex: 1.8 },
    ],
    sampleRows: [],
    detailItems: [
      { label: 'Category', value: 'Legacy but still active' },
      { label: 'Families', value: 'cruise and campaign' },
      { label: 'Migration order', value: 'Remaining legacy-active modules' },
    ],
    timeline: commonTimeline,
  },
  legacyGiftcardOperations: {
    key: 'legacyGiftcardOperations',
    title: 'Legacy Giftcard Operations',
    shortTitle: 'Giftcards',
    path: PATH_DASHBOARD.legacy.giftcardOperations.root,
    navLabel: 'Giftcard Ops',
    navGroup: 'legacy',
    icon: 'solar:gift-bold',
    mode: 'legacy',
    description:
      'Legacy-active giftcard operations shell, including completion and dispute flows with documented auto-settlement semantics.',
    routeGroups: [
      'GET /v2/admin/giftcard_orders',
      'GET /v2/admin/giftcard_orders/find',
      'GET /v2/admin/giftcard_orders/details',
      'POST /v2/admin/giftcard_orders/complete',
      'POST /v2/admin/giftcard_orders/reject',
      'POST /v2/admin/giftcard_orders/dispute',
    ],
    statuses: ['pending', 'completed', 'rejected', 'disputed'],
    bullets: [
      'Giftcard completion still has documented outbox-related auto-settlement behavior.',
      'This module is legacy-active, not deprecated, and should remain reachable.',
      'Do not conflate giftcard settlement semantics with the refactored policy engine.',
    ],
    sampleColumns: [
      { field: 'surface', headerName: 'Surface', flex: 1.2 },
      { field: 'status', headerName: 'Status', flex: 0.8 },
      { field: 'notes', headerName: 'Notes', flex: 1.8 },
    ],
    sampleRows: [
      {
        id: 'giftcard-complete',
        surface: 'complete',
        status: 'completed',
        notes: 'Auto-settlement may hand off to outbox when configured',
      },
    ],
    detailItems: [
      { label: 'Category', value: 'Legacy but still active' },
      { label: 'Payout note', value: 'Completion may create outbox dispatch intent' },
      { label: 'Migration order', value: 'Remaining legacy-active modules' },
    ],
    timeline: commonTimeline,
  },
  legacyCompliance: {
    key: 'legacyCompliance',
    title: 'Legacy Compliance',
    shortTitle: 'Compliance',
    path: PATH_DASHBOARD.legacy.compliance.root,
    navLabel: 'Compliance',
    navGroup: 'legacy',
    icon: 'solar:clipboard-check-bold',
    mode: 'legacy',
    description:
      'Legacy-active compliance queues and review routes that remain separate from the refactored risk and user-control modules.',
    routeGroups: [
      'GET /v2/admin/compliance/queue',
      'GET /v2/admin/compliance/overview',
      'GET /v2/admin/compliance/health',
      'GET /v2/admin/compliance/history',
      'GET /v2/admin/compliance/review/{userId}',
      'POST /v2/admin/compliance/address/approve/{id}',
      'POST /v2/admin/compliance/address/reject/{id}',
    ],
    statuses: ['queued', 'pending', 'approved', 'rejected'],
    bullets: [
      'Keep this accessible only because the contract still marks it active.',
      'Do not resurrect inactive kyc_one or kyc_ng list variants.',
      'Refactored risk and user-control flows should remain separate from legacy compliance review.',
    ],
    sampleColumns: [
      { field: 'surface', headerName: 'Surface', flex: 1.2 },
      { field: 'status', headerName: 'Status', flex: 0.8 },
      { field: 'notes', headerName: 'Notes', flex: 1.8 },
    ],
    sampleRows: [],
    detailItems: [
      { label: 'Category', value: 'Legacy but still active' },
      { label: 'Explicitly removed', value: 'inactive user list variants' },
      { label: 'Migration order', value: 'Remaining legacy-active modules' },
    ],
    timeline: commonTimeline,
  },
  legacyUserManagement: {
    key: 'legacyUserManagement',
    title: 'Legacy User Management',
    shortTitle: 'Legacy Users',
    path: PATH_DASHBOARD.legacy.userManagement.root,
    navLabel: 'User Management',
    navGroup: 'legacy',
    icon: 'solar:user-bold',
    mode: 'legacy',
    description:
      'Legacy-active user-management routes preserved while the new Users module centers on controls and primary-account review.',
    routeGroups: [
      'GET /v2/admin/users',
      'GET /v2/admin/users/find',
      'GET /v2/admin/users/wallet',
      'GET /v2/admin/user/get_user/{userId}',
      'POST /v2/admin/user/invite_kyc_ng',
      'POST /v2/admin/user/update',
      'GET /v2/admin/user/referrals',
      'GET /v2/admin/user/invites',
      'GET /v2/admin/user/transactions',
      'GET /v2/admin/user/balance_audit',
      'POST /v2/admin/user/update_setting',
      'POST /v2/admin/user/suspend_user',
      'GET /v2/admin/user/details',
      'POST /v2/admin/user/update_profile',
    ],
    statuses: ['active', 'blocked'],
    bullets: [
      'Keep reachable for operational continuity while the refactored Users module grows.',
      'Avoid mixing legacy user profile actions into new controls and primary-account review screens.',
      'Do not call the explicitly inactive KYC list variants.',
    ],
    sampleColumns: [
      { field: 'surface', headerName: 'Surface', flex: 1.2 },
      { field: 'status', headerName: 'Status', flex: 0.8 },
      { field: 'notes', headerName: 'Notes', flex: 1.8 },
    ],
    sampleRows: [],
    detailItems: [
      { label: 'Category', value: 'Legacy but still active' },
      { label: 'Preferred refactor area', value: 'Users' },
      { label: 'Migration order', value: 'Remaining legacy-active modules' },
    ],
    timeline: commonTimeline,
  },
  legacySystemReports: {
    key: 'legacySystemReports',
    title: 'Legacy System Reports',
    shortTitle: 'System Reports',
    path: PATH_DASHBOARD.legacy.systemReports.root,
    navLabel: 'System Reports',
    navGroup: 'legacy',
    icon: 'solar:chart-bold',
    mode: 'legacy',
    description:
      'Legacy-active operational reports and system variable routes, kept separate from the new Config and Service Reports shells.',
    routeGroups: [
      'GET /v2/admin/sys_variables',
      'POST /v2/admin/sys_variables',
      'GET /v2/admin/referral_ranking',
      'GET /v2/admin/monthly_bonus',
      'GET /v2/admin/activities',
    ],
    statuses: ['active'],
    bullets: [
      'System variables remain active, but new configuration work should target the refactored Config module first.',
      'Referral ranking, monthly bonus, and activities remain accessible as legacy-active reports.',
      'This placeholder keeps those routes discoverable without promoting deprecated flows.',
    ],
    sampleColumns: [
      { field: 'surface', headerName: 'Surface', flex: 1.2 },
      { field: 'status', headerName: 'Status', flex: 0.8 },
      { field: 'notes', headerName: 'Notes', flex: 1.8 },
    ],
    sampleRows: [],
    detailItems: [
      { label: 'Category', value: 'Legacy but still active' },
      { label: 'Preferred refactor area', value: 'Config or Service Reports depending on use case' },
      { label: 'Migration order', value: 'Remaining legacy-active modules' },
    ],
    timeline: commonTimeline,
  },
  legacyBlockchainViews: {
    key: 'legacyBlockchainViews',
    title: 'Legacy Blockchain Views',
    shortTitle: 'Blockchain',
    path: PATH_DASHBOARD.legacy.blockchainViews.root,
    navLabel: 'Blockchain Views',
    navGroup: 'legacy',
    icon: 'solar:database-bold',
    mode: 'legacy',
    description:
      'Legacy-active blockchain visibility routes retained alongside the refactored Service Reports and blockchain ops overview.',
    routeGroups: [
      'GET /v2/admin/blockchain/quidax_accounts',
      'GET /v2/admin/blockchain/quidax_accounts/find',
      'GET /v2/admin/blockchain/wallet_addresses',
      'GET /v2/admin/blockchain/wallet_addresses/find',
      'GET /v2/admin/blockchain/instant_swaps',
      'GET /v2/admin/blockchain/instant_swaps/find',
    ],
    statuses: ['active'],
    bullets: [
      'Keep accessible because the contract still marks these views active.',
      'Refactored blockchain visibility also exists under service and blockchain ops routes.',
      'This area should not revive deprecated transaction or order screens.',
    ],
    sampleColumns: [
      { field: 'surface', headerName: 'Surface', flex: 1.2 },
      { field: 'status', headerName: 'Status', flex: 0.8 },
      { field: 'notes', headerName: 'Notes', flex: 1.8 },
    ],
    sampleRows: [],
    detailItems: [
      { label: 'Category', value: 'Legacy but still active' },
      { label: 'Refactored companion', value: 'Service Reports' },
      { label: 'Migration order', value: 'Remaining legacy-active modules' },
    ],
    timeline: commonTimeline,
  },
};

export const moduleOrder = [
  'dashboard',
  'accessControl',
  'riskPolicy',
  'users',
  'transactions',
  'settlementOps',
  'blockchainOps',
  'queueJobs',
  'sos',
  'adjustments',
  'config',
  'serviceReports',
  'legacyAdminManagement',
  'legacyCryptoAssets',
  'legacyContent',
  'legacyCruiseCampaigns',
  'legacyGiftcardOperations',
  'legacyCompliance',
  'legacyUserManagement',
  'legacySystemReports',
  'legacyBlockchainViews',
];

export const navGroups = [
  {
    subheader: 'refactored shell',
    keys: ['dashboard', 'accessControl', 'riskPolicy'],
  },
  {
    subheader: 'operations',
    keys: ['users', 'transactions', 'settlementOps', 'blockchainOps', 'queueJobs', 'sos', 'adjustments'],
  },
  {
    subheader: 'platform',
    keys: ['config', 'serviceReports'],
  },
  {
    subheader: 'legacy active',
    keys: [
      'legacyAdminManagement',
      'legacyCryptoAssets',
      'legacyContent',
      'legacyCruiseCampaigns',
      'legacyGiftcardOperations',
      'legacyCompliance',
      'legacyUserManagement',
      'legacySystemReports',
      'legacyBlockchainViews',
    ],
  },
];
